import { useEffect, useRef, useState, type ReactElement } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import {
  editorConfig,
  EditorRefPlugin,
  PasteFilterPlugin,
  SpoilerPlugin,
  TGPEPastePlugin,
  usePreviewStore,
} from "@tgpe/editor-core";
import type { LexicalEditor } from "lexical";
import { useTranslation } from "react-i18next";

import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";

import { useStorage } from "../../lib/storage/storage-context.js";
import { useEditorInstance } from "../../stores/editor-instance.js";
import { useMediaStore } from "../../stores/media-store.js";
import { showToast } from "../../stores/toast-store.js";

import { MediaPreviewSlot } from "../media/MediaPreviewSlot.js";

import { useAutosave } from "./hooks/useAutosave.js";
import { useInitialState } from "./hooks/useInitialState.js";
import { LinkInsertPlugin } from "./LinkInsertPlugin.js";
import { PreviewBridgePlugin } from "./preview-bridge.js";
import { Toolbar } from "./Toolbar.js";

function PersistencePlugins(): null {
  const storage = useStorage();
  useInitialState();
  useAutosave(storage);
  return null;
}

/**
 * TGPE clipboard paste handler that surfaces success / version-mismatch
 * outcomes via the in-app toast store.
 */
function TGPEClipboardPastePlugin(): ReactElement {
  const { t } = useTranslation();
  return (
    <TGPEPastePlugin
      onPayloadParsed={(payload) => {
        showToast(
          t("clipboard.paste_success", { client: payload.source_client }),
          "success",
        );
      }}
      onSchemaVersionMismatch={(got) => {
        showToast(t("clipboard.paste_version_mismatch", { got }), "error");
      }}
    />
  );
}

/**
 * Publishes the live LexicalEditor into the editor-instance store so
 * non-Lexical surfaces (share controller, MainButton wiring) can read
 * the current state without prop drilling.
 */
function PublishEditorInstancePlugin(): null {
  const [editor] = useLexicalComposerContext();
  const setEditor = useEditorInstance((s) => s.setEditor);
  useEffect(() => {
    setEditor(editor);
    return () => {
      setEditor(null);
    };
  }, [editor, setEditor]);
  return null;
}

/**
 * Mirrors the presence of a media attachment into the editor-core preview
 * store: when media is attached, switch to `caption` mode (1024 limit);
 * otherwise back to `message` (4096). The counter under the editor and
 * the `TelegramPreview` both pick up the change automatically.
 */
function MediaModeSyncPlugin(): null {
  const hasMedia = useMediaStore((s) => s.attachment !== null);
  const setMode = usePreviewStore((s) => s.setMode);
  useEffect(() => {
    setMode(hasMedia ? "caption" : "message");
  }, [hasMedia, setMode]);
  return null;
}

function EditorCounter() {
  const charCount = usePreviewStore((s) => s.state?.charCount ?? 0);
  const mode = usePreviewStore((s) => s.mode);
  const captionLimit = usePreviewStore((s) => s.captionLimit);
  const limit = mode === "message" ? 4096 : captionLimit;
  const pct = limit > 0 ? (charCount / limit) * 100 : 0;
  const clampedPct = Math.min(100, pct);
  const overLimit = pct > 100;
  const textColor = overLimit
    ? "text-red-600 font-bold"
    : pct > 95
      ? "text-red-600"
      : pct >= 80
        ? "text-yellow-600"
        : "text-[var(--tg-hint)]";
  const barColor = overLimit
    ? "bg-red-600"
    : pct > 95
      ? "bg-red-500"
      : pct >= 80
        ? "bg-yellow-500"
        : "bg-[var(--tg-link)]";
  return (
    <div className="shrink-0 border-t border-[var(--tg-secondary-bg)]">
      <div className="h-0.5 w-full bg-[var(--tg-secondary-bg)]">
        <div
          aria-hidden
          className={`h-full ${barColor} transition-all duration-150`}
          style={{ width: `${String(clampedPct)}%` }}
        />
      </div>
      <div className="flex items-center justify-end px-3 py-1 text-xs">
        <span className={`tabular-nums ${textColor}`}>
          {charCount.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function Placeholder() {
  return (
    <div className="pointer-events-none absolute left-3 top-3 select-none text-[var(--tg-hint)]">
      Write your Telegram post…
    </div>
  );
}

export function EditorPanel() {
  const editorRef = useRef<LexicalEditor | null>(null);
  const linkButtonRef = useRef<HTMLButtonElement | null>(null);
  const wa = useTelegramWebApp();

  // Track Telegram's stable viewport height so the soft keyboard on iOS
  // doesn't push our content off-screen. We constrain the editor area to
  // viewport_stable_height when available.
  const [stableHeight, setStableHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!wa) return;
    setStableHeight(wa.viewportStableHeight);
    const handler = (): void => {
      setStableHeight(wa.viewportStableHeight);
    };
    wa.onEvent("viewportChanged", handler);
    return () => {
      wa.offEvent("viewportChanged", handler);
    };
  }, [wa]);

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div
        className="flex h-full flex-col"
        style={stableHeight ? { maxHeight: stableHeight } : undefined}
      >
        <Toolbar linkButtonRef={linkButtonRef} />
        <MediaPreviewSlot />
        <div className="relative flex-1 overflow-y-auto bg-[var(--tg-bg)]">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="min-h-full p-3 text-[var(--tg-text)] outline-none"
                aria-label="Telegram post editor"
              />
            }
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <EditorCounter />
        <LinkInsertPlugin anchorRef={linkButtonRef} />
      </div>
      <HistoryPlugin />
      <LinkPlugin />
      <ListPlugin />
      <AutoFocusPlugin />
      <SpoilerPlugin />
      <PasteFilterPlugin />
      <EditorRefPlugin editorRef={editorRef} />
      <PreviewBridgePlugin />
      <PersistencePlugins />
      <PublishEditorInstancePlugin />
      <MediaModeSyncPlugin />
      <TGPEClipboardPastePlugin />
    </LexicalComposer>
  );
}
